import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, BellOff, Store, Building2, MapPin, Tag, Loader2, Heart, Search } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { useSubscriptions } from "../context/SubscriptionsContext";
import { useLang } from "../context/LanguageContext";
import { useToast } from "../hooks/use-toast";
import { UndoAction } from "../components/UndoAction";

const Row = ({ icon: Icon, title, sub, meta, to, onUnfollow, testid, unfollowLabel }) => (
  <div className="flex items-center gap-3 bg-card border border-border rounded-2xl p-4" data-testid={testid}>
    <span className="h-10 w-10 shrink-0 rounded-xl bg-[#e7f6ec] dark:bg-[#16341f] flex items-center justify-center text-[#0e7a3c] dark:text-[#7ee0a3]">
      <Icon size={18} />
    </span>
    <div className="min-w-0 flex-1">
      <Link to={to} className="font-bold text-foreground hover:text-[#16a34a] transition-colors block truncate">{title}</Link>
      {sub && <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><MapPin size={11} /> {sub}</p>}
    </div>
    {meta != null && (
      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-bold text-[#0e7a3c] dark:text-[#7ee0a3] bg-[#e7f6ec] dark:bg-[#16341f] px-2.5 py-1 rounded-full">
        <Tag size={11} /> {meta}
      </span>
    )}
    <button onClick={onUnfollow} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-rose-500 border border-border rounded-xl px-3 py-2 transition-colors">
      <BellOff size={14} /> <span className="hidden sm:inline">{unfollowLabel}</span>
    </button>
  </div>
);

const Following = () => {
  const { user } = useAuth();
  const { t } = useLang();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { subs, spots, loaded, toggleFollow, toggleFollowSpot } = useSubscriptions();
  const [counts, setCounts] = useState({});
  const [searches, setSearches] = useState([]);
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    if (!subs.length) return;
    api.get("/deals").then((r) => {
      const c = {};
      (r.data || []).forEach((d) => { c[d.business] = (c[d.business] || 0) + 1; });
      setCounts(c);
    }).catch(() => {});
  }, [subs.length]);

  useEffect(() => {
    if (!user) return;
    api.get("/my/searches").then((r) => setSearches(r.data || [])).catch(() => setSearches([]));
    api.get("/my/areas").then((r) => setAreas(r.data || [])).catch(() => setAreas([]));
  }, [user]);

  const dropArea = async (id, label) => {
    const removed = areas.find((a) => a.id === id);
    setAreas((p) => p.filter((a) => a.id !== id));
    try { await api.delete(`/my/areas/${id}`); } catch { /* keep the optimistic removal */ }
    toast({
      title: t("map.areaRemoved", { area: label }),
      action: <UndoAction testid="undo-area-alert" onUndo={async () => {
        if (!removed) return;
        try {
          const { data } = await api.post("/my/areas", { label: removed.label, lat: removed.lat, lng: removed.lng, radiusKm: removed.radiusKm, category: removed.category });
          setAreas((p) => (p.some((x) => x.id === data.id) ? p : [data, ...p]));
        } catch { toast({ title: t("undo.failed") }); }
      }} />,
    });
  };

  const restoreSearch = async (q) => {
    try {
      const { data } = await api.post("/my/searches", { q });
      setSearches((p) => (p.some((x) => x.id === data.id) ? p : [{ id: data.id, q }, ...p]));
    } catch { toast({ title: t("undo.failed") }); }
  };

  const dropSearch = async (id, q) => {
    setSearches((p) => p.filter((x) => x.id !== id));
    try { await api.delete(`/my/searches/${id}`); } catch { /* keep the optimistic removal */ }
    toast({ title: t("saved.removed", { q }), action: <UndoAction testid="undo-saved-search" onUndo={() => restoreSearch(q)} /> });
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="following-signin">
        <p className="text-lg font-bold text-foreground">{t("follows.signIn")}</p>
        <button onClick={() => navigate("/signup")} className="mt-4 bg-[#16a34a] hover:bg-[#0e7a3c] text-white font-bold px-5 py-2.5 rounded-xl transition-colors">
          {t("follows.createAccount")}
        </button>
      </div>
    );
  }

  if (!loaded) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#16a34a]" size={30} /></div>;

  const empty = subs.length === 0 && spots.length === 0 && searches.length === 0 && areas.length === 0;

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 lg:py-8 space-y-8" data-testid="following-page">
      <header>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-foreground flex items-center gap-2.5">
          <Bell className="text-[#16a34a]" size={28} /> {t("follows.title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1.5">{t("follows.subtitle")}</p>
      </header>

      {empty && (
        <div className="bg-card border border-dashed border-border rounded-2xl p-10 text-center" data-testid="following-empty">
          <Heart className="mx-auto text-[#16a34a] mb-3" size={26} />
          <p className="font-bold text-foreground">{t("follows.emptyTitle")}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("follows.emptyBody")}</p>
          <div className="flex flex-wrap justify-center gap-2 mt-5">
            <Link to="/businesses" className="bg-[#16a34a] hover:bg-[#0e7a3c] text-white text-sm font-bold px-4 py-2.5 rounded-xl transition-colors">{t("nav.businesses")}</Link>
            <Link to="/lytemap" className="bg-secondary hover:bg-[#e7f6ec] dark:hover:bg-[#16341f] text-foreground text-sm font-bold px-4 py-2.5 rounded-xl transition-colors">{t("nav.lytemap")}</Link>
          </div>
        </div>
      )}

      {subs.length > 0 && (
        <section data-testid="followed-businesses">
          <h2 className="text-base md:text-lg font-bold text-foreground flex items-center gap-2 mb-3">
            <Store size={17} className="text-[#16a34a]" /> {t("follows.businesses", { n: subs.length })}
          </h2>
          <div className="space-y-2.5">
            {subs.map((b) => (
              <Row key={b} icon={Store} title={b} to={`/business/${encodeURIComponent(b)}`}
                meta={counts[b] != null ? t("spot.dealCount", { n: counts[b] }) : null}
                testid={`followed-business-${b}`}
                unfollowLabel={t("follows.unfollow")}
                onUnfollow={async () => {
                  await toggleFollow(b);
                  toast({ title: t("follows.unfollowed", { name: b }), action: <UndoAction testid="undo-unfollow" onUndo={() => toggleFollow(b)} /> });
                }} />
            ))}
          </div>
        </section>
      )}

      {searches.length > 0 && (
        <section data-testid="saved-searches">
          <h2 className="text-base md:text-lg font-bold text-foreground flex items-center gap-2 mb-3">
            <Search size={17} className="text-[#16a34a]" /> {t("saved.section", { n: searches.length })}
          </h2>
          <div className="space-y-2.5">
            {searches.map((sq) => (
              <Row key={sq.id} icon={Search} title={`“${sq.q}”`} to={`/browse?q=${encodeURIComponent(sq.q)}`}
                testid={`saved-search-${sq.q.replace(/\s+/g, "-")}`}
                onUnfollow={() => dropSearch(sq.id, sq.q)} />
            ))}
          </div>
        </section>
      )}

      {spots.length > 0 && (
        <section data-testid="followed-spots">
          <h2 className="text-base md:text-lg font-bold text-foreground flex items-center gap-2 mb-3">
            <Building2 size={17} className="text-[#16a34a]" /> {t("follows.places", { n: spots.length })}
          </h2>
          <div className="space-y-2.5">
            {spots.map((s) => (
              <Row key={s.coord} icon={Building2} title={s.name || s.address || t("spot.fallbackTitle")}
                sub={s.name ? s.address : ""} to={`/spot/${s.coord}`}
                meta={s.dealCount != null ? t("spot.dealCount", { n: s.dealCount }) : null}
                testid={`followed-spot-${s.coord}`}
                unfollowLabel={t("follows.unfollow")}
                onUnfollow={async () => {
                  await toggleFollowSpot(s.coord);
                  toast({ title: t("follows.unfollowed", { name: s.name || s.address }), action: <UndoAction testid="undo-unfollow-spot" onUndo={() => toggleFollowSpot(s.coord)} /> });
                }} />
            ))}
          </div>
        </section>
      )}
      {areas.length > 0 && (
        <section data-testid="saved-areas">
          <h2 className="text-base md:text-lg font-bold text-foreground flex items-center gap-2 mb-3">
            <MapPin size={17} className="text-[#16a34a]" /> {t("map.areasSection", { n: areas.length })}
          </h2>
          <div className="space-y-2.5">
            {areas.map((a) => (
              <Row key={a.id} icon={MapPin}
                title={a.category ? `${a.label} · ${a.category}` : a.label}
                sub={t("map.areaRadius", { km: Math.round(a.radiusKm) })}
                to={`/lytemap?lat=${a.lat}&lng=${a.lng}&z=13${a.category ? `&cat=${encodeURIComponent(a.category)}` : ""}`}
                meta={a.dealCount != null ? t("spot.dealCount", { n: a.dealCount }) : null}
                testid={`saved-area-${a.id}`}
                unfollowLabel={t("follows.unfollow")}
                onUnfollow={() => dropArea(a.id, a.label)} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default Following;
